import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  IconButton,
  Typography,
} from '@mui/material'

import ErrorIcon from '@mui/icons-material/Error'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { SuccessIcon, LinkIcon } from 'src/assets/icons'
import { useMe } from 'src/hooks'
import { IHealthCheck } from 'src/types'
import { AppEvents } from 'src/constants'

import { getTableTimestamp } from 'src/common/utils'

import { useStyles } from './styles'
import { ResponseForm } from './response-form.component'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { Box } from '@mui/system'

interface IProps {
  items?: IHealthCheck[] | void
}

export const TableView = ({ items }: IProps) => {
  const [t] = useTranslation()
  const [formInitValues, setFormInitValues] = useState<Object | null>(null)
  const { data: user } = useMe()
  const classes = useStyles()

  const theme = useTheme()

  const handleOpen = (val: IHealthCheck) => () => {
    setFormInitValues(val.response)
  }

  const handleClose = () => {
    setFormInitValues(null)
  }

  const handleCopyClick = (link?: string) => {
    if (!link) {
      return
    }

    const detail = { message: t('clipboard-copied-notification.v') }
    const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
    document.dispatchEvent(event)

    navigator.clipboard.writeText(link)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0 } }}>
              <TableCell>API</TableCell>
              <TableCell align="center">
                <HelperWrapper t="account-users-status-column" />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="email-logs-date-column" />
              </TableCell>
              <TableCell>
                <HelperWrapper t="third-party-api-endpoint-link-column" />
              </TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map((row) => (
              <TableRow
                key={row.name}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
                sx={{ 'td, th': { border: 0 } }}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {row.success ? (
                      <SuccessIcon className={classes.successIcon} />
                    ) : (
                      <ErrorIcon className={classes.failureIcon} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {getTableTimestamp({ date: row.timestamp, timezone: user?.timezone })}
                </TableCell>
                <TableCell sx={{ maxWidth: '400px' }}>
                  {row?.endpoint_link && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          wordBreak: 'break-all',
                          color: theme.palette.custom.grey2,
                        }}
                      >
                        {row.endpoint_link}
                      </Typography>
                      <Box sx={{ minWidth: 64 }}>
                        <IconButton
                          sx={{
                            padding: '0.375rem',
                          }}
                          disabled={!row?.endpoint_link}
                          onClick={() => {
                            handleCopyClick(row?.endpoint_link)
                          }}
                        >
                          <ContentCopyIcon className={classes.linkIcon} />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: '0.375rem',
                          }}
                          disabled={false}
                          component="a"
                          href={
                            row.endpoint_link?.includes('http')
                              ? row.endpoint_link
                              : `//${row.endpoint_link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkIcon className={classes.linkIcon} />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center">
                  {!row.success && (
                    <Button type="button" onClick={handleOpen(row)}>
                      {t('response-button-text.v')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ResponseForm values={formInitValues} open={!!formInitValues} handleClose={handleClose} />
    </>
  )
}
